<template>
	<div class="flow-emoji-picker-wrapper" :inline="inline" @click="handleClick">
		<PopOver :open.sync="openPicker" :overlay="false">
			<div
				class="flow-emoji-selected"
				tabindex="-1"
				:effects="effects"
				:state="state"
				:data-placeholder="selectedHtml ? '' : placeholder"
				@click="openPicker = true"
			>
				<span>{{ selectedHtml }}</span>
				<Icon
					v-if="selectedHtml !== '' && selectedHtml !== null && selectedHtml !== undefined"
					name="i-close"
					type="filled"
					:effects="false"
					size="x-small"
					@click.stop="handleSelect(null)"
				/>
				<Icon
					v-if="state === 'error'"
					:state="'error'"
					:size="'small'"
					:effects="false"
					name="i-info-fill"
					type="filled"
				/>
			</div>

			<template #content>
				<div class="flow-emoji-picker">
					<Picker
						:title="title"
						:native="true"
						:include="include"
						:exclude="exclude"
						:custom="custom"
						:emoji="'point_up'"
						:recent="recent"
						@select="handleSelect"
						@skin-change="handleSkinChange"
					/>
				</div>
			</template>
		</PopOver>
	</div>
</template>
<script lang="ts">
import { defineComponent } from "vue";
//@ts-expect-error
import { Picker } from "emoji-mart-vue";
import Icon from "../Icon/Icon.vue";
import PopOver from "../PopOver/PopOver.vue";

type EmojiObj = {
	id: string;
	name: string;
	colons: string;
	text: string;
	emoticons: string[];
	skin: number | null;
	native: string;
};

export default defineComponent({
	name: "EmojiPicker",
	components: {
		Picker,
		PopOver,
		Icon
	},
	model: {
		prop: "selected",
		event: "input"
	},
	props: {
		/**
		 * true | false <br/> Used to enable/disable :hover, :active, :focus effects <br/> Default : true
		 */
		effects: {
			type: Boolean,
			default: true
		},
		recent: {
			type: Array,
			default: () => [
				"office",
				// "compass"
				// "luggage"
				"hourglass",
				"gem",
				"computer",
				"cd",
				"floppy_disk",
				"movie_camera",
				"book",
				"scroll",
				"moneybag",
				"credit_card",
				"package",
				"file_folder",
				"bar_chart",
				"round_pushpin",
				"lock",
				"key",
				"hammer_and_wrench",
				"gear",
				"link",
				// "magnet",
				"earth_africa",
				"earth_americas",
				"earth_asia"
			]
		},
		/**
		 * title displays at bottom of picker
		 */
		title: {
			type: String,
			default: "Pick project icon..."
		},
		placeholder: {
			type: String,
			default: ""
		},
		// default: {
		// 	type: Object,
		// 	default: () => {
		// 		return {
		// 			colons: ":package:",
		// 			id: "package",
		// 			name: "Package",
		// 			native: "📦",
		// 			skin: null,
		// 			unified: "1f4e6"
		// 		};
		// 	}
		// },
		/**
		 * true | false
		 */
		inline: {
			type: Boolean,
			default: false
		},
		/**
		 * true | false
		 */
		closeOnSelect: {
			type: Boolean,
			default: true
		},
		/**
		 * Only load included categories.
		 */
		include: {
			type: Array,
			default: () => []
		},
		/**
		 * Don't load excluded categories.
		 */
		exclude: {
			type: Array,
			default: () => []
		},
		custom: {
			type: Array,
			default: () => []
		},
		selected: {
			type: [Object, String],
			default: () => null
		},
		/**
		 * 'default' | 'error'
		 */
		state: {
			type: String,
			default: "default"
		}
	},
	data() {
		return {
			openPicker: false
		};
	},
	computed: {
		selectedHtml() {
			if (this.selected && typeof this.selected === "object") {
				return this.selected.native;
			} else if (this.selected && typeof this.selected === "string") {
				return this.selected;
			}
			return null;
		}
	},
	methods: {
		handleSelect(emoji: EmojiObj | null) {
			if (this.closeOnSelect) {
				this.openPicker = false;
			}

			/**
			 * Whenever value is updated.<br/>
			 * use 'selected.sync' in parent component to achieve 2 way binding.
			 *
			 * @event update:selected
			 */
			this.$emit("update:selected", emoji);

			/**
			 * Whenever emoji selected
			 *
			 * @event select
			 */
			this.$emit("select", emoji);
			/**
			 * Whenever emoji selected
			 *
			 * @event input
			 */
			this.$emit("input", emoji ? emoji.native : null);
		},
		handleSkinChange(skin: number) {
			this.openPicker = false;
			/**
			 * Whenever skin changed
			 *
			 * @event skinChange
			 */
			this.$emit("skinChange", skin);
		},
		handleClick(e: MouseEvent) {
			/**
			 * Click event
			 *
			 * @event click
			 */
			this.$emit("click", e);
		}
	}
});
</script>
<style lang="scss">
@mixin emoji-colors(
	$font-dark,
	$font-light,
	$font,
	$element-light,
	$element-dark,
	$border,
	$primary,
	$input,
	$input-placeholder,
	$input-focus
) {
	div.flow-emoji-picker-wrapper {
		div.flow-emoji-picker {
			> div.emoji-mart {
				color: $font-dark;
				background: $element-light;
				border: none;
				> div.emoji-mart-bar {
					border: 0px solid $border;

					> div.emoji-mart-preview {
						> div.emoji-mart-preview-data {
							> span.emoji-mart-title-label {
								color: $font-light;
							}
							> div.emoji-mart-preview-name {
								color: $font-dark;
							}
							> div.emoji-mart-preview-shortnames {
								* {
									color: $font-light;
								}
							}
							> div.emoji-mart-preview-emoticons {
								* {
									color: $font-light;
								}
							}
						}

						> div.emoji-mart-preview-skins {
							> div.emoji-mart-skin-swatches {
								border: 1px solid $font;
								background-color: transparent;
							}
						}
					}

					> div.emoji-mart-anchors {
						svg {
							fill: $font-light;
						}

						span.emoji-mart-anchor-bar {
							background-color: $primary !important;
						}
					}
				}

				> div.emoji-mart-search {
					> input {
						border: 1px solid $border;

						background: $input;
						color: $font;
						&::placeholder {
							color: $input-placeholder;
						}

						&:focus {
							box-shadow: inset 0 0 0 1px $input-focus;
						}
					}
				}

				> div.emoji-mart-scroll {
					> div.emoji-mart-category {
						> div.emoji-mart-category-label {
							span {
								color: $font-dark;
								background: $element-light;
								// background: transparent;
							}
						}

						> span.emoji-mart-emoji {
							&:before {
								background-color: transparent;
								border: 1px solid $element-dark;
							}
						}
						div.emoji-mart-no-results-label {
							color: $font-light;
						}
					}
				}
			}
		}

		div.flow-emoji-selected {
			background-color: $input;

			&:focus {
				box-shadow: inset 0 0 0 1px $primary;
			}

			&:before {
				color: $input-placeholder;
			}
			&[state="error"] {
				box-shadow: 0 0 0 1px var(--dashboard-danger);
			}
		}
	}
}

div.flow-emoji-picker-wrapper {
	&[inline] {
		div.v-popover {
			width: fit-content;
			div.trigger {
				width: fit-content;
			}
		}
	}
	div.v-popover {
		width: 100%;
		div.trigger {
			width: 100%;
		}
		div.popover-inner {
			background-color: transparent !important;
			padding: 0px;
		}
	}
	div.flow-emoji-picker {
		box-shadow: 0 1px 4px rgba(0, 0, 0, 0.4), 0 0 40px rgba(0, 0, 0, 0.1) inset;
		> div.emoji-mart {
			border: none;
			> div.emoji-mart-bar {
				border-bottom-width: 1px !important;
				&:last-child {
					border-bottom-width: 0px !important;
					border-top-width: 1px !important;
				}

				> div.emoji-mart-preview {
					height: 52px;
					> div.emoji-mart-preview-emoji {
						left: 0px;

						> span.emoji-mart-emoji {
							> span {
								font-size: 30px !important;
								width: 30px !important;
								height: 30px !important;
							}
						}
					}
					> div.emoji-mart-preview-data {
						left: 42px;
						> span.emoji-mart-title-label {
							font-size: 14px;
							font-family: var(--body-font);
						}
						> div.emoji-mart-preview-name {
							font-family: var(--body-font);
							font-size: 12px;
						}
						> div.emoji-mart-preview-shortnames {
							// color: var(--dashboard-font-light);
							// font-family: var(--body-font);
							// font-size: 10px;
							* {
								font-family: var(--body-font);
								font-size: 10px;
							}
						}
						> div.emoji-mart-preview-emoticons {
							display: none;
							// color: var(--dashboard-font-light);
							// font-family: var(--body-font);
							// font-size: 10px;
							* {
								font-family: var(--body-font);
								font-size: 10px;
							}
						}
					}

					> div.emoji-mart-preview-skins {
						right: 18px;

						> div.emoji-mart-skin-swatches {
							background-color: transparent;
							display: none;
						}
					}
				}
			}

			> div.emoji-mart-search {
				padding: 8px;
				> input {
					font-family: var(--body-font);
					padding: 10px 14px 10px 14px;
					font-size: 12px;
					line-height: 12px;
					height: 30px;
					border-radius: 15px;
					display: flex;
					align-items: center;
					&::placeholder {
						font-family: var(--body-font) !important;
					}
				}
			}

			> div.emoji-mart-scroll {
				> div.emoji-mart-category {
					> div.emoji-mart-category-label {
						span {
							font-family: var(--body-font);
							font-weight: 300;
							font-size: 12px;
							line-height: 14px;
							margin-top: 2px;
							margin-bottom: 2px;
							padding: 4px 6px;
						}
					}

					div.emoji-mart-no-results-label {
						font-family: var(--body-font);
						font-size: 12px;
					}
				}
			}
		}
	}

	div.flow-emoji-selected {
		min-height: 36px;
		min-width: 60px;
		font-size: 24px;
		cursor: pointer;
		flex: auto;
		width: 100%;
		border-radius: 4px;
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: 6px 8px;
		outline: none;

		&[effects] {
			&:active {
				filter: contrast(80%);
			}

			&:hover:not(:active) {
				filter: contrast(75%);
			}
		}
		position: relative;
		&:before {
			position: absolute;
			content: attr(data-placeholder);
			top: 0px;
			left: 0px;
			right: 0px;
			bottom: 0px;
			display: flex;
			font-size: 12px;
			align-items: center;
			padding: 11px 12px 11px 12px;
			font-weight: normal;
			line-height: 14px;
			overflow: hidden;
		}

		> * + * {
			margin-left: 8px;
		}
	}
}

@include emoji-colors(
	var(--dashboard-font-dark),
	var(--dashboard-font-light),
	var(--dashboard-font),
	var(--dashboard-element-light),
	var(--dashboard-element-dark),
	var(--dashboard-border-light),
	var(--dashboard-primary),
	var(--dashboard-input),
	var(--dashboard-input-placeholder),
	var(--dashboard-input-focus)
);
div.sideout-container {
	@include emoji-colors(
		var(--slideout-font-dark),
		var(--slideout-font-light),
		var(--slideout-font),
		var(--slideout-element-light),
		var(--slideout-element-dark),
		var(--slideout-border),
		var(--slideout-primary),
		var(--slideout-input),
		var(--slideout-input-placeholder),
		var(--slideout-input-focus)
	);
}
</style>
